import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { computed, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ViewStateRecord } from '@common/data-models/records/viewstate-record';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { NAVIGATION_STORAGE_KEY } from '@common/enums/constants';
import { NavigationActions } from '@common/store/navigation/actions/navigation.actions';
import { NavigationGroup, NavigationGroupTypes, NavigationItem, NavigationItemTypes, NavigationLayoutTypes, NavigationSection } from '@common/store/navigation/models/navigation.models';
import { selectCurrentItem } from '@common/store/navigation/selectors/navigation.selectors';
import { Store } from '@ngrx/store';
import { DataStore } from '@src/suite/common/data-models/data-store/data-store';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { concatMap, from, map, Observable, Subject, throwError } from 'rxjs';

import { AsyncServiceBase } from './async-service-base';
import { CustomCardFieldsService } from './custom-card-fields-service';
import { GlobalTablesService } from './global-tables-service';
import { LanguageSettings } from './language-settings';
import { LayoutState } from './layout-state';
import { NomWebServicesJson } from './nom-web-services-json';
import { VisualStateService } from './visual-state-service';

@Injectable({ providedIn: 'root' })
export class NavigationService extends AsyncServiceBase {
    public inEditTeamSettingsMode: boolean = false;
    public enterprisePlaybookTeam: string;
    public exitingTeamSettingsMode: boolean = false;
    public currentPlaybookTeam: string;
    public prevPlaybookTeam: string;

    // New view properties
    public newViewShown: { [id: string]: any } = { flag: false, event: null };
    private newViewShownSource = new Subject<any>();
    public newViewShownObservable = this.newViewShownSource.asObservable();

    // New template properties
    public newTemplateShown: { [id: string]: any } = { flag: false, event: null };
    private newTemplateShownSource = new Subject<any>();
    public newTemplateShownObservable = this.newTemplateShownSource.asObservable();

    public fromNotification: boolean = false;

    public isAboutshown: any = { flag: false, event: null };
    public showAboutSource = new Subject<any>();
    public showAboutObservable = this.showAboutSource.asObservable();

    public currentListView: any = null;

    item = computed(() => this.store.selectSignal(selectCurrentItem)());
    vs = computed(() => this.store.selectSignal(selectCurrentViewState)());

    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public customCardFieldsService: CustomCardFieldsService,
        public languageSettings: LanguageSettings,
        public viewStateService: LayoutState,
        public router: Router,
        public ngZone: NgZone,
        private store: Store
    ) {
        super();

        this.globalTablesService.globalTablesLoaded.subscribe(() => {
            this.getNavigationAndResolve();
        });

        if (!window['NavigateToItem']) {
            window['NavigateToItem'] = () => {
                this.ngZone.run(async () => {
                    try {
                        const navigationData = localStorage.getItem('navigationData');
                        if (navigationData) {
                            const objNavigationData = JSON.parse(navigationData);
                            this.hideAbout(null);
                            // let submenu: string = (this.CurrentNavigationItem.SectionPath) ? this.CurrentNavigationItem.SectionPath : "tmp";
                            // let item: string = (this.CurrentNavigationItem.ViewState.guid) ? this.CurrentNavigationItem.ViewState.guid : "tmp";
                            localStorage.removeItem('navigationData');
                            this.fromNotification = true;
                            this.router.navigate(['/PST', 'playbook', 'system', 'item', objNavigationData.Guid]);
                        }
                    } catch (e) {
                        console.log('Failed to navigate to item ...');
                        console.log(e.message);
                    }
                });
            };
        }
    }

    public async getNavigationAndResolve(): Promise<any> {
        if (this.visualStateService.isDesktop) {
            this.initializeNavigationForDesktop();
        } else {
            this.initializeNavigation();
        }
        this.initializeResolve(null);
    }

    public initializeNavigation() {
        if (!this.currentPlaybookTeam) this.currentPlaybookTeam = this.nomWebServicesJson.HelloResults.HomeProgram || this.globalTablesService.DefaultPlaybookTeam.ID;
        if (localStorage.getItem(NAVIGATION_STORAGE_KEY) !== null) {
            this.store.dispatch(NavigationActions.loadNavigationData());
        } else if (localStorage.getItem('Navigation')) {
            this.store.dispatch(NavigationActions.initializeNavigationData());
        }

        this.store.dispatch(NavigationActions.storeNavigationData());
    }

    public initializeNavigationForDesktop() {}

    public loadTeamNavigation(team: string): Observable<unknown> {
        return from(
            this.nomWebServicesJson.get('/api/PSTNavigation', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
                params: {
                    team,
                },
            })
        ).pipe(
            map(res => {
                return res.body;
            })
        );
    }

    public updateNavigation(application: string, type: NavigationLayoutTypes, team: string, sections: Array<NavigationSection>): Observable<boolean> {
        let response: Observable<boolean> = null;

        try {
            response = from(
                this.nomWebServicesJson.put(
                    '/api/PSTNavigation/SaveNavigation',
                    {
                        Application: application,
                        Navigation: sections,
                        NavigationLayoutType: type,
                        Team: team || '',
                    },
                    {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json',
                            'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                        }),
                        responseType: 'json',
                        observe: 'response',
                    }
                )
            ).pipe(
                map(res => {
                    return !!res.body;
                })
            );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public async setDefaultNavigationItem(guid: string, layout: NavigationLayoutTypes, application: string, team: string): Promise<ServiceResponse<boolean>> {
        let response: HttpResponse<any> = null;

        try {
            response = await this.nomWebServicesJson.put(
                '/api/PSTNavigation/SetDefaultView',
                {
                    guid: guid,
                    layout: layout,
                    application: application,
                    Team: team || '',
                },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                    }),
                    responseType: 'json',
                    observe: 'response',
                }
            );
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to set default navigation view state',
                detailMessage: `Unable to set default navigation view state!. \n\rStackTrace: NomWebServicesJson.setDefaultView(); \n\rError: ${error}`,
            });
            return new ServiceResponse<boolean>(false, `Unable to set default navigation view state ${error.toString()}`);
        }
        return new ServiceResponse<boolean>(true, response.body);
    }

    public saveCurrentNavigationItem(): Observable<ServiceResponse<ViewStateRecord>> {
        if (this.item()) {
            if (this.currentListView) this.currentListView.showLoader();
            let res: Observable<ServiceResponse<ViewStateRecord>> = null;
            if (this.isSystemSearch(this.item()) || this.isNewView(this.item())) {
                if (this.isSystemSearch(this.item())) {
                    this.store.dispatch(ViewStateActions.updateViewStateSearchTitle());
                    const path = `U_${this.nomWebServicesJson.UserId}\\VS`;
                    res = from(this.viewStateService.createViewStateRecord(this.vs(), path));
                } else {
                    res = from(this.viewStateService.createViewStateRecord(this.vs()));
                }
            } else {
                res = from(this.viewStateService.updateViewStateRecord(this.vs()));
            }

            if (this.currentListView) this.currentListView.hideLoader();
            return res;
        }
        return null;
    }

    public updateViewStateRecord(vs: ViewStateRecord): Observable<ViewStateRecord> {
        return from(DataStore.set(vs)).pipe(
            concatMap(() => {
                return from(this.viewStateService.updateViewStateRecord(vs)).pipe(
                    map(res => {
                        return res.status ? res.data : null;
                    })
                );
            })
        );
    }

    public createViewStateRecord(group: number, _section: number, vs: ViewStateRecord): Observable<ViewStateRecord> {
        let path = '';
        if (group === NavigationGroupTypes.Global) {
            path = `P_${this.globalTablesService.EnterprisePlaybookGroup}\\VS`;
        }
        if (group === NavigationGroupTypes.Team) {
            if (this.currentPlaybookTeam) path = `P_${this.currentPlaybookTeam}\\VS`;
            else path = `P_${this.globalTablesService.DefaultPlaybookTeam.ID}\\VS`;
        }
        if (group === NavigationGroupTypes.Personal || !group) {
            path = `U_${this.nomWebServicesJson.UserId}\\VS`;
        }
        vs.fullPath = path;

        return from(this.viewStateService.createViewStateRecord(vs)).pipe(
            map(res => {
                return res.status ? res.data : null;
            })
        );
    }

    public getLayout(group: NavigationGroup): NavigationLayoutTypes {
        let layout = NavigationLayoutTypes.User;
        if (group.id === NavigationGroupTypes.Global) {
            layout = NavigationLayoutTypes.Enterprise;
        }
        if (group.id === NavigationGroupTypes.Team) {
            layout = NavigationLayoutTypes.Group;
        }

        return layout;
    }

    public getTeam(group: NavigationGroup): string {
        let team = '';
        if (group.id === NavigationGroupTypes.Team) {
            team = this.currentPlaybookTeam;
        }
        return team;
    }

    // About menu
    public showAbout(event: any) {
        this.isAboutshown = { flag: true, event };
        this.showAboutSource.next(this.isAboutshown);
    }

    public hideAbout(event: any) {
        this.isAboutshown = { flag: false, event };
        this.showAboutSource.next(this.isAboutshown);
    }

    // Create new view
    public openCreateNewView(event) {
        this.newViewShown = { flag: true, event };
        this.newViewShownSource.next(this.newViewShown);
    }

    public closeCreateNewView(event: any) {
        this.newViewShown = { flag: false, event };
        this.newViewShownSource.next(this.newViewShown);
    }

    // Create new template
    public openCreateNewTemplate(event) {
        this.newTemplateShown = { flag: true, event };
        this.newTemplateShownSource.next(this.newViewShown);
    }

    public closeCreateNewTemplate(event: any) {
        this.newTemplateShown = { flag: false, event };
        this.newTemplateShownSource.next(this.newViewShown);
    }

    public async saveTemplateSettings(templateRecord) {
        this.newTemplateShown = { flag: true, event: true, templateRecord };
        this.newTemplateShownSource.next(this.newTemplateShown);
    }

    public isSystemSearch(item: NavigationItem): boolean {
        return item && item.guid === NavigationItemTypes.SYSTEM_SEARCH_GUID;
    }

    public isNewView(item: NavigationItem): boolean {
        return item && item.guid === NavigationItemTypes.SYSTEM_NEW_VIEW_GUID && !item.viewState.guid;
    }
}
